/**
 * React Query hooks for the Rides Dashboard.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosClient } from '../lib/api.ts';
import { NO_FILTERS, filterParams, type RideFilters } from './useRideFilters.ts';
import type {
  RideDetail,
  RideSamplesResponse,
  RideSummaryResponse,
  RidesListResponse,
} from '../types/api.ts';

const STALE_MS = 60_000;

/** ``rides``: cycling only (the API default); ``all``: runs, swims, gym too. */
export type RideScope = 'rides' | 'all';

/** ``scope=all`` only when asked: ride-only requests stay as they were. */
function scopeParam(scope: RideScope): { scope?: 'all' } {
  return scope === 'all' ? { scope } : {};
}

export function useRides(limit = 20, scope: RideScope = 'rides', filters: RideFilters = NO_FILTERS) {
  const filter = filterParams(filters, scope);
  return useQuery<RidesListResponse>({
    queryKey: ['rides', { limit, scope, ...filter }],
    queryFn: () =>
      axiosClient
        .get('/rides', { params: { limit, ...scopeParam(scope), ...filter } })
        .then((r) => r.data),
    staleTime: STALE_MS,
  });
}

/**
 * Fetch every cycling ride in a closed date window (e.g. one calendar
 * month). Used by the Rides Calendar view to populate per-day cells.
 *
 * Backend caps at 100 rows in a single window; that's enough for any
 * realistic month. ``from``/``to`` are ``YYYY-MM-DD`` strings (inclusive
 * on both ends).
 */
export function useRidesInRange(
  from: string | null,
  to: string | null,
  scope: RideScope = 'rides',
  filters: RideFilters = NO_FILTERS,
) {
  const filter = filterParams(filters, scope);
  return useQuery<RidesListResponse>({
    queryKey: ['rides', 'range', from, to, scope, filter],
    queryFn: () =>
      axiosClient
        .get('/rides', { params: { from, to, ...scopeParam(scope), ...filter } })
        .then((r) => r.data),
    enabled: from !== null && to !== null,
    staleTime: STALE_MS,
  });
}

export function useRide(id: number | null) {
  return useQuery<RideDetail>({
    queryKey: ['rides', id],
    queryFn: () => axiosClient.get(`/rides/${id}`).then((r) => r.data),
    enabled: id !== null && Number.isFinite(id),
    staleTime: STALE_MS,
  });
}

export function useRideSamples(id: number | null, hasSamples: boolean) {
  return useQuery<RideSamplesResponse>({
    queryKey: ['rides', id, 'samples'],
    queryFn: () =>
      axiosClient.get(`/rides/${id}/samples`).then((r) => r.data),
    enabled: id !== null && Number.isFinite(id) && hasSamples,
    staleTime: STALE_MS,
  });
}

export function useAnalyzeRide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rideId: number) =>
      axiosClient
        .post(`/rides/${rideId}/analyze`)
        .then((r) => r.data as RideSummaryResponse),
    onSuccess: (_data, rideId) => {
      qc.invalidateQueries({ queryKey: ['rides'] });
      qc.invalidateQueries({ queryKey: ['rides', rideId] });
    },
  });
}

export function useRefreshRideZones() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rideId: number) =>
      axiosClient
        .post(`/rides/${rideId}/zones/refresh`)
        .then((r) => r.data as { hr_buckets: unknown; power_buckets: unknown }),
    onSuccess: (_data, rideId) => {
      qc.invalidateQueries({ queryKey: ['rides', rideId] });
    },
  });
}
